import { SPRITE_SLOTS, darken } from './palette';

const PUBLIC = process.env.PUBLIC_URL || '';

/**
 * One recoloured sprite sheet per distinct appearance.
 *
 * WHY THIS IS POSSIBLE AT ALL: the generated sheets use exactly nine flat,
 * un-antialiased colours (see `scripts/generate-placeholder-sprites.mjs` and
 * the `hero.palette.json` it writes). Every skin pixel is byte-identical, so
 * "replace skin with this player's tone" is an exact-match remap — no
 * tolerance, no blending, no artefacts. Soft-edged art would make this
 * impossible and force layered sheets instead.
 *
 * TWO LEVELS OF CACHE, because both are shared:
 *   - the base sheets, keyed by geometry (body + hair style). Six exist; two
 *     players with the same build download one.
 *   - the recoloured canvases, keyed by the full appearance. A room where
 *     everyone picked the defaults holds one canvas, not forty.
 *
 * Recolouring is a single 128x128 pass, done once per distinct look and never
 * per frame.
 */

/** @type {Map<string, HTMLImageElement>} sheet name -> loading/loaded image */
const sheets = new Map();

/** @type {Map<string, HTMLCanvasElement>} appearance key -> recoloured sheet */
const variants = new Map();

/** Which generated sheet a look needs. Mirrors `sheetFor` on the server. */
export function sheetNameFor(appearance) {
  const body = appearance?.body || 'slim';
  const hairStyle = appearance?.hairStyle || 'short';
  return `hero-${body}-${hairStyle}`;
}

/** Stable identity for a look, so identical players share one canvas. */
export function appearanceKey(appearance) {
  if (!appearance) return 'default';
  const {
    body, hairStyle, skin, hair, shirt, pants, shoes,
  } = appearance;
  return [body, hairStyle, skin, hair, shirt, pants, shoes].join('|');
}

/** Load a base sheet once, shared across every player using that geometry. */
function loadSheet(name) {
  const existing = sheets.get(name);
  if (existing) return existing;

  const image = new Image();
  // getImageData below taints the canvas for a cross-origin source. Same origin
  // today, but being explicit costs nothing and the failure would be obscure.
  image.crossOrigin = 'anonymous';
  image.src = `${PUBLIC}/sprites/${name}.png`;
  sheets.set(name, image);
  return image;
}

const hexToRgb = (hex) => {
  const m = /^#([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  /* eslint-disable no-bitwise */
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  /* eslint-enable no-bitwise */
};

/**
 * Build the sentinel -> replacement map for one appearance.
 *
 * The shaded slots are DERIVED, not chosen. The art shades sleeves, the back
 * leg and the mouth a step darker than their base; asking a player to pick both
 * a shirt colour and its shadow would be absurd, so `darken` computes it.
 *
 * `line` is deliberately absent: the outline stays dark because it is what
 * keeps the character legible against the near-black arena floor.
 */
function buildRemap(appearance) {
  const pairs = [
    [SPRITE_SLOTS.skin, appearance.skin],
    [SPRITE_SLOTS.skinDark, darken(appearance.skin)],
    [SPRITE_SLOTS.hair, appearance.hair],
    [SPRITE_SLOTS.shirt, appearance.shirt],
    [SPRITE_SLOTS.shirtDark, darken(appearance.shirt)],
    [SPRITE_SLOTS.pants, appearance.pants],
    [SPRITE_SLOTS.pantsDark, darken(appearance.pants)],
    [SPRITE_SLOTS.shoe, appearance.shoes],
  ];

  const remap = new Map();
  pairs.forEach(([from, to]) => {
    const source = hexToRgb(from);
    const target = hexToRgb(to);
    if (source && target) remap.set(source.join(','), target);
  });
  return remap;
}

/**
 * The recoloured sheet for an appearance, or null while the base is still
 * loading.
 *
 * Returning null rather than blocking is deliberate: the caller falls back to
 * the shared sheet for a frame or two, which is invisible, whereas waiting
 * would stall the render loop.
 */
export function variantFor(appearance) {
  if (!appearance) return null;

  const key = appearanceKey(appearance);
  const cached = variants.get(key);
  if (cached) return cached;

  const image = loadSheet(sheetNameFor(appearance));
  // `complete` alone is true for a failed load too; naturalWidth is the check
  // that distinguishes "decoded" from "404".
  if (!image.complete || !image.naturalWidth) return null;

  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;

  ctx.drawImage(image, 0, 0);
  const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = frame;
  const remap = buildRemap(appearance);

  for (let i = 0; i < data.length; i += 4) {
    // Transparent pixels carry junk RGB and must not be matched — an outline
    // colour hiding under alpha 0 would otherwise be "recoloured" invisibly.
    if (data[i + 3] !== 0) {
      const to = remap.get(`${data[i]},${data[i + 1]},${data[i + 2]}`);
      if (to) {
        [data[i], data[i + 1], data[i + 2]] = to;
      }
    }
  }

  ctx.putImageData(frame, 0, 0);
  variants.set(key, canvas);
  return canvas;
}

/** Drop every cached canvas. Used by tests; the maps are module-level. */
export function clearVariantCache() {
  variants.clear();
  sheets.clear();
}
